/**
  * Normalize label position from the config.
  * Returns an object with numeric x and y properties, or null if the input is invalid.
  */
export function normalizeLabelPosition(point: { x: number | string; y: number | string } | undefined) {
  if (!point) return null;

  const x = Number(point.x);
  const y = Number(point.y);

  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

  return { x, y };
}

export type LabelSelectionPoint = { x: number; y: number; rotation?: number };

export function toSelectionPoint(latlng: { lat: number; lng: number }): LabelSelectionPoint {
  return { x: latlng.lat, y: latlng.lng };
}

export function appendSelectionPoint(
  points: LabelSelectionPoint[],
  point: LabelSelectionPoint,
) {
  const lastPoint = points[points.length - 1];
  const sameAsLastPoint = lastPoint &&
    lastPoint.x === point.x &&
    lastPoint.y === point.y;
  if (sameAsLastPoint) return points;

  return [...points, point];
}

export function isPointOnSegment(
  point: LabelSelectionPoint,
  start: LabelSelectionPoint,
  end: LabelSelectionPoint,
) {
  const cross = (point.y - start.y) * (end.x - start.x) - (point.x - start.x) * (end.y - start.y);
  if (Math.abs(cross) > 1e-9) {
    return false;
  }

  const dot = (point.x - start.x) * (end.x - start.x) + (point.y - start.y) * (end.y - start.y);
  if (dot < 0) {
    return false;
  }

  const squaredLength = (end.x - start.x) ** 2 + (end.y - start.y) ** 2;
  return dot <= squaredLength;
}

export function isWithinSelectionLasso(
  point: LabelSelectionPoint,
  polygon: LabelSelectionPoint[],
) {
  if (polygon.length < 3) {
    return false;
  }

  let inside = false;

  for (let index = 0, previousIndex = polygon.length - 1; index < polygon.length; previousIndex = index++) {
    const current = polygon[index];
    const previous = polygon[previousIndex];

    if (isPointOnSegment(point, previous, current)) {
      return true;
    }

    const intersects = ((current.y > point.y) !== (previous.y > point.y))
      && (point.x < (((previous.x - current.x) * (point.y - current.y)) / ((previous.y - current.y) || Number.EPSILON)) + current.x);

    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
}

export function debugLabelMap(message: string, details: unknown) {
  if (process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'test') {
    return;
  }

  console.debug(`[labels/map] ${message}`, details);
}


